import React from "react";
import { useTheme } from "react-jss";
import { Scrollbars } from "react-custom-scrollbars";
import classnames from "classnames";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "./FontAwesome";
import useStyles from "./ScrollableContainer.styles";

interface Props {
    children?: any;
}

enum ScrollDirection {
    Left = -1,
    Right = 1,
}

function ScrollableIndicator({ onClick, direction, showIndicator }) {
    const theme = useTheme();
    const classes = useStyles({ theme });

    const [icon, indicatorClass] =
        direction === ScrollDirection.Left
            ? ["caret-left", classes.indicatorLeft]
            : ["caret-right", classes.indicatorRight];

    return (
        <div
            className={classnames(
                classes.indicator,
                indicatorClass,
                !showIndicator && classes.indicatorHidden,
            )}
            onClick={onClick}
        >
            <FontAwesomeIcon icon={icon as IconProp} className={classes.icon} />
        </div>
    );
}

const defaultScrollValues = { scrollLeft: 0, scrollWidth: 0, clientWidth: 0 };
const deadzone = 29;

export default function ScrollableContainer(props: Props) {
    const theme = useTheme();
    const classes = useStyles({ theme });
    const scrollbarRef = React.useRef(undefined);
    const [hover, setHover] = React.useState(false);
    const [scrolling, setScrolling] = React.useState(false);
    const [showScrollbar, setShowScrollbar] = React.useState(false);
    const [showLeftScrollIndicator, setShowLeftScrollIndicator] = React.useState(false);
    const [showRightScrollIndicator, setShowRightScrollIndicator] = React.useState(false);
    const [{ scrollLeft, scrollWidth, clientWidth }, setScrollValues] = React.useState(
        defaultScrollValues,
    );
    const { children } = props;

    React.useEffect(() => {
        if (hover || scrolling) setShowScrollbar(true);
        else {
            const timeout = setTimeout(() => setShowScrollbar(false), 200);
            return function cleanup() {
                clearTimeout(timeout);
            };
        }
        return undefined;
    }, [hover, scrolling]);

    const scrollBy = React.useCallback((value: number) => {
        if (scrollbarRef.current.view)
            scrollbarRef.current.view.scrollBy({
                left: value,
                behavior: "smooth",
            });
    }, []);

    React.useEffect(() => {
        const isContainerNarrow = clientWidth < scrollWidth;
        const hasMoreLeft = scrollLeft > deadzone;
        const shouldShowLeftScrollIndicator = isContainerNarrow && hasMoreLeft;

        setShowLeftScrollIndicator(shouldShowLeftScrollIndicator);

        const hasMoreRight = scrollLeft + clientWidth < scrollWidth - deadzone;
        const shouldShowRightScrollIndicator = isContainerNarrow && hasMoreRight;

        setShowRightScrollIndicator(shouldShowRightScrollIndicator);
    }, [clientWidth, scrollWidth, scrollLeft]);

    function scrollPage(direction: ScrollDirection) {
        scrollBy(clientWidth * direction);
    }

    return (
        <div
            className={classes.scrollable}
            onMouseOver={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            <ScrollableIndicator
                onClick={() => scrollPage(ScrollDirection.Left)}
                direction={ScrollDirection.Left}
                showIndicator={showLeftScrollIndicator}
            />
            <Scrollbars
                className={classnames(classes.scrollbars)}
                ref={scrollbarRef}
                autoHeight
                autoHeightMax={Number.MAX_SAFE_INTEGER}
                onUpdate={setScrollValues}
                onScrollStart={() => setScrolling(true)}
                onScrollStop={() => setScrolling(false)}
                hideTracksWhenNotNeeded
                renderTrackHorizontal={(trackProps) => (
                    <div
                        {...trackProps}
                        className={classnames(
                            classes.scrollTrackHorizontal,
                            !showScrollbar && classes.scrollTrackHorizontalHidden,
                        )}
                    />
                )}
                renderThumbHorizontal={(thumbProps) => (
                    <div {...thumbProps} className={classes.scrollThumbHorizontal} />
                )}
                renderView={(viewProps) => {
                    if (viewProps.style) {
                        viewProps.style = { ...viewProps.style, overflow: "visible" };
                    }
                    return <div {...viewProps} className={classes.scrollableWindow} />;
                }}
            >
                <div className={classes.scrollableChild}>{children}</div>
            </Scrollbars>
            <ScrollableIndicator
                onClick={() => scrollPage(ScrollDirection.Right)}
                direction={ScrollDirection.Right}
                showIndicator={showRightScrollIndicator}
            />
        </div>
    );
}
